import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Download, Edit, ExternalLink, Image, RotateCcw, Trash2 } from "lucide-react";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { BrandedQRCode } from "@/components/StyledQRCode";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateAgentUrlName } from "@/hooks/agent";
import { useQRGenerator } from "@/hooks/embed/useQRGenerator";
import { useGenieProducts } from "@/hooks/marketplace/useGenieProducts";
import { useQRCodeDelete, useQRCodeRedownload } from "@/hooks/qr-code/useSavedQRCodes";
import { Activity, activityService } from "@/services/analytics/ActivityService";
import { productManualUrl } from "@/services/marketplace/ProductGenieService";
import { Agent } from "@/types/agent/agent";
import {
  DEFAULT_QR_DISPLAY_CONFIG,
  QR_LABEL_OPTIONS,
  QR_STYLE_OPTIONS,
  QRDisplayConfig,
  QRDisplayStyle,
  QRDownloadFormat,
  QRLabelOption,
} from "@/types/ui/branding";

interface QRCodeModalProps {
  genie: Agent | null;
  isOpen: boolean;
  onClose: () => void;
}

/** What scanning the code opens: the genie's own page, or one product's Manual 2.0 page. */
type QRTarget = "genie" | "product";

const QR_TARGET_OPTIONS: { value: QRTarget; label: string; hint: string }[] = [
  { value: "genie", label: "Genie page", hint: "Talk to the genie" },
  { value: "product", label: "Product manual", hint: "Answers from the official manual" },
];

const QRCodeModal = ({ genie, isOpen, onClose }: QRCodeModalProps) => {
  const navigate = useNavigate();
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editedUrlName, setEditedUrlName] = useState("");
  const [urlError, setUrlError] = useState("");
  const [urlHistory, setUrlHistory] = useState<Activity[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [isInviteMode, setIsInviteMode] = useState(false);

  // QR display configuration state - initialize with genie's brand colors
  const getDefaultConfigForGenie = (agent: Agent | null): QRDisplayConfig => ({
    ...DEFAULT_QR_DISPLAY_CONFIG,
    foregroundColor: agent?.branding?.qrCodeStyle?.foregroundColor || "#000000",
    backgroundColor: agent?.branding?.qrCodeStyle?.backgroundColor || "#FFFFFF",
  });

  const [displayConfig, setDisplayConfig] = useState<QRDisplayConfig>(() => getDefaultConfigForGenie(genie));
  const [downloadFormat, setDownloadFormat] = useState<QRDownloadFormat>("png");

  // Reset to brand defaults when modal opens or genie changes
  useEffect(() => {
    if (isOpen && genie) {
      setDisplayConfig(getDefaultConfigForGenie(genie));
    }
  }, [isOpen, genie?.id]);

  // A marketplace genie serves catalogue products; each of those has a Manual 2.0
  // page the QR can open instead of the genie page. Other genies never see this.
  const { data: products = [] } = useGenieProducts(isOpen ? genie?.id : null);
  const [qrTarget, setQrTarget] = useState<QRTarget>("genie");
  const [productSlug, setProductSlug] = useState<string>("");
  useEffect(() => {
    setQrTarget("genie");
    setProductSlug("");
  }, [genie?.id]);
  useEffect(() => {
    if (products.length > 0 && !products.some((product) => product.slug === productSlug)) {
      setProductSlug(products[0].slug);
    }
  }, [products, productSlug]);
  const selectedProduct = products.find((product) => product.slug === productSlug) ?? null;
  const isProductTarget = qrTarget === "product" && !!selectedProduct;

  const qrBaseUrl = window.location.origin;
  const genieUrlName = genie?.url_name ?? genie?.page?.url_name;
  const genieQrValue = genieUrlName
    ? `${qrBaseUrl}/live-agent/${genieUrlName}?ref=qr${isInviteMode ? "&type=invite" : ""}`
    : null;
  const qrValue = isProductTarget ? productManualUrl(selectedProduct.slug) : genieQrValue;

  const { downloadQR } = useQRGenerator(qrValue || "", genie!, { size: 400 });
  const updateUrlMutation = useUpdateAgentUrlName(setUrlHistory);
  const redownloadMutation = useQRCodeRedownload();
  const deleteMutation = useQRCodeDelete();
  useEffect(() => {
    if (isOpen && genie?.id) {
      setIsLoadingHistory(true);
      activityService.getQrUrlChangeHistory(genie.id, 10).then((history) => {
        setUrlHistory(history);
        setIsLoadingHistory(false);
      });
    }
  }, [isOpen, genie?.id]);

  const handleEditUrl = () => {
    setEditedUrlName(genie?.url_name ?? genie?.page?.url_name ?? "");
    setIsEditingUrl(true);
    setUrlError("");
  };

  const handleSaveUrl = async () => {
    if (!editedUrlName.trim() || !genie?.id) return;

    setUrlError("");

    try {
      await updateUrlMutation.mutateAsync({ agent: genie, urlName: editedUrlName });
      setIsEditingUrl(false);
    } catch (error) {
      setUrlError(error instanceof Error ? error.message : "Failed to update URL");
    }
  };

  const handleCancelEdit = () => {
    setEditedUrlName(genie?.url_name ?? genie?.page?.url_name ?? "");
    setIsEditingUrl(false);
    setUrlError("");
  };

  const handleDownloadQR = async () => {
    if (!qrValue) return;

    try {
      await downloadQR(downloadFormat, displayConfig);
    } catch (error) {
      console.error("Error downloading QR code:", error);
      alert("There was an error downloading the QR code. Please try again.");
    }
  };

  const updateDisplayConfig = (updates: Partial<QRDisplayConfig>) => {
    setDisplayConfig((prev) => ({ ...prev, ...updates }));
  };

  if (!isOpen || !genie) return null;

  if (!genieUrlName) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" showClose={false} className="w-full sm:max-w-xl flex flex-col h-full p-0">
          <div className="flex items-center justify-between border-b p-6 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900">QR Code Not Available</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
            >
              &times;
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-8 py-6 text-center">
            <p className="text-gray-600 mb-6">
              This genie needs to be fully set up before a QR code can be generated. Please complete the setup process
              first.
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`QR Code - ${genie.name}`}
      description="Customize and download your QR code."
      className="sm:!max-w-2xl md:!max-w-3xl lg:!max-w-4xl"
    >
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Left Column - Options */}
          <div className="space-y-5 order-2 lg:order-1">
            {/* QR target: only a genie with catalogue products gets the choice */}
            {products.length > 0 && (
              <div data-testid="qr-target-section">
                <label className="text-sm font-semibold text-gray-700 mb-2 block">QR Opens</label>
                <div className="grid grid-cols-2 gap-2">
                  {QR_TARGET_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      data-testid={`qr-target-${option.value}`}
                      onClick={() => setQrTarget(option.value)}
                      className={`p-2 rounded-lg border-2 text-left transition-all ${
                        qrTarget === option.value
                          ? "border-gray-200 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <span
                        className={`text-xs font-medium block ${
                          qrTarget === option.value
                            ? "bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent"
                            : "text-gray-900"
                        }`}
                      >
                        {option.label}
                      </span>
                      <span className="text-[10px] text-gray-500 block">{option.hint}</span>
                    </button>
                  ))}
                </div>
                {qrTarget === "product" && (
                  <div className="mt-2">
                    <Select value={productSlug} onValueChange={setProductSlug}>
                      <SelectTrigger className="w-full border-gray-300 h-auto py-2" data-testid="qr-product-select">
                        <SelectValue placeholder="Choose a product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.slug}>
                            {product.name}
                            {product.model_number && product.model_number !== product.name ? ` · ${product.model_number}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] text-gray-500">
                      Scanning opens this product's manual page, with answers cited from the official manual.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Style Selection */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Style</label>
              <div className="grid grid-cols-4 gap-2">
                {QR_STYLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateDisplayConfig({ style: option.value as QRDisplayStyle })}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${
                      displayConfig.style === option.value
                        ? "border-gray-200 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span
                      className={`text-xs font-medium ${
                        displayConfig.style === option.value
                          ? "bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent"
                          : "text-gray-900"
                      }`}
                    >
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Label Selection */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Label Text</label>
              <div className="grid grid-cols-2 gap-2">
                {QR_LABEL_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateDisplayConfig({ label: option.value as QRLabelOption })}
                    className={`p-2 rounded-lg border-2 text-left transition-all ${
                      displayConfig.label === option.value
                        ? "border-gray-200 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span
                      className={`text-xs font-medium ${
                        displayConfig.label === option.value
                          ? "bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent"
                          : "text-gray-900"
                      }`}
                    >
                      {option.value === "talk-to-genie"
                        ? `Talk to ${genie.name.length > 10 ? genie.name.slice(0, 10) + "..." : genie.name}`
                        : option.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Colors - Two inline inputs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">Colors</label>
                <button
                  onClick={() => setDisplayConfig(getDefaultConfigForGenie(genie))}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-genie-primary transition-colors"
                  title="Reset to brand defaults"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">QR Code</label>
                  <div className="flex items-center gap-2 border-2 border-gray-200 rounded-lg p-2 bg-white">
                    <label className="relative cursor-pointer shrink-0">
                      <div
                        className="w-6 h-6 rounded-md border border-gray-300 shadow-sm"
                        style={{ backgroundColor: displayConfig.foregroundColor }}
                      />
                      <input
                        type="color"
                        value={displayConfig.foregroundColor}
                        onChange={(e) => updateDisplayConfig({ foregroundColor: e.target.value })}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      />
                    </label>
                    <input
                      type="text"
                      value={displayConfig.foregroundColor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(val) || val === "") {
                          updateDisplayConfig({ foregroundColor: val || "#000000" });
                        }
                      }}
                      className="flex-1 text-xs font-mono bg-transparent border-none outline-none uppercase"
                      placeholder="#000000"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Background</label>
                  <div className="flex items-center gap-2 border-2 border-gray-200 rounded-lg p-2 bg-white">
                    <label className="relative cursor-pointer shrink-0">
                      <div
                        className="w-6 h-6 rounded-md border border-gray-300 shadow-sm"
                        style={{ backgroundColor: displayConfig.backgroundColor }}
                      />
                      <input
                        type="color"
                        value={displayConfig.backgroundColor}
                        onChange={(e) => updateDisplayConfig({ backgroundColor: e.target.value })}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      />
                    </label>
                    <input
                      type="text"
                      value={displayConfig.backgroundColor}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(val) || val === "") {
                          updateDisplayConfig({ backgroundColor: val || "#FFFFFF" });
                        }
                      }}
                      className="flex-1 text-xs font-mono bg-transparent border-none outline-none uppercase"
                      placeholder="#FFFFFF"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Toggles and Format - Inline */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 block">Options</label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={displayConfig.showLogo}
                    onCheckedChange={(checked) => updateDisplayConfig({ showLogo: checked })}
                  />
                  <span className="text-xs text-gray-700">Show logo</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={displayConfig.transparentBackground}
                    onCheckedChange={(checked) => updateDisplayConfig({ transparentBackground: checked })}
                  />
                  <span className="text-xs text-gray-700">Transparent</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Format</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDownloadFormat("png")}
                    className={`flex-1 p-2 rounded-lg border-2 text-center transition-all ${
                      downloadFormat === "png"
                        ? "border-gray-200 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span
                      className={`text-xs font-medium ${downloadFormat === "png" ? "bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent" : "text-gray-900"}`}
                    >
                      PNG
                    </span>
                  </button>
                  <button
                    onClick={() => setDownloadFormat("svg")}
                    className={`flex-1 p-2 rounded-lg border-2 text-center transition-all ${
                      downloadFormat === "svg"
                        ? "border-gray-200 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span
                      className={`text-xs font-medium ${downloadFormat === "svg" ? "bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent" : "text-gray-900"}`}
                    >
                      SVG
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* URL Section */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">
                {isProductTarget ? "Product Manual URL" : "Public Page URL"}
              </label>
              {isProductTarget ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <p className="text-xs text-gray-700 truncate" title={qrValue ?? ""} data-testid="qr-product-url">
                    {qrValue}
                  </p>
                </div>
              ) : isEditingUrl ? (
                <div className="space-y-2">
                  <div className="flex items-center border border-gray-300 rounded-lg p-2">
                    <span className="text-xs text-gray-500 mr-1">.../</span>
                    <input
                      type="text"
                      value={editedUrlName}
                      onChange={(e) => setEditedUrlName(e.target.value)}
                      className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900"
                      placeholder="your-custom-url"
                    />
                  </div>
                  {urlError && <p className="text-red-600 text-xs">{urlError}</p>}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={updateUrlMutation.isPending}
                      className="text-gray-500"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveUrl}
                      disabled={updateUrlMutation.isPending || !editedUrlName.trim()}
                      className="bg-genie-primary hover:bg-genie-primary-hover text-white"
                    >
                      {updateUrlMutation.isPending ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <div className="flex items-center justify-between gap-2">
                    <p
                      className="text-xs text-gray-700 truncate flex-1"
                      title={`${qrBaseUrl}/live-agent/${genieUrlName}`}
                    >
                      .../{genieUrlName}
                    </p>
                    <button
                      onClick={handleEditUrl}
                      className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* URL History (Collapsible) */}
            {urlHistory.length > 0 && (
              <div>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="w-full flex items-center justify-between text-xs text-gray-500 hover:text-gray-700"
                >
                  <span>URL History ({urlHistory.length})</span>
                  {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {showHistory && (
                  <div className="mt-2 space-y-1 max-h-20 overflow-y-auto">
                    {urlHistory.slice(0, 3).map((activity) => (
                      <div key={activity.id} className="text-xs bg-gray-50 p-1.5 rounded">
                        <span className="text-gray-600">
                          {activity.metadata?.oldUrl} → {activity.metadata?.newUrl}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Saved QR Codes Gallery */}
            {genie?.saved_qr_codes && genie.saved_qr_codes.length > 0 && (
              <div>
                <button
                  onClick={() => setShowGallery(!showGallery)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-gray-900"
                >
                  <span className="flex items-center gap-1.5">
                    <Image className="w-3.5 h-3.5" />
                    Saved QR Codes ({genie.saved_qr_codes.length})
                  </span>
                  {showGallery ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showGallery && (
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {genie.saved_qr_codes.map((qr) => (
                      <div
                        key={qr.id}
                        className="relative group border border-gray-200 rounded-lg p-2 hover:border-genie-primary/50 transition-colors"
                      >
                        <img
                          src={qr.storage_url}
                          alt={`QR - ${qr.display_config.style}`}
                          className="w-full h-24 object-contain rounded"
                        />
                        <div className="flex items-center justify-between mt-1.5">
                          <div className="min-w-0">
                            <p className="text-[10px] font-medium text-gray-700 capitalize truncate">
                              {qr.display_config.style}
                            </p>
                            <p className="text-[9px] text-gray-400">
                              {qr.format.toUpperCase()} &middot; {new Date(qr.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => redownloadMutation.mutate(qr)}
                              className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-genie-primary transition-colors"
                              title="Re-download"
                            >
                              <Download className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => deleteMutation.mutate(qr.id)}
                              className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Preview (Always at top on mobile, right side on desktop) */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-0">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">Preview</label>
              <Button
                onClick={handleDownloadQR}
                size="sm"
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-sm"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Download {downloadFormat.toUpperCase()}
              </Button>
            </div>
            <div
              className="flex items-center justify-center p-4 rounded-xl mx-auto"
              style={{
                backgroundColor: displayConfig.transparentBackground ? "#f0f0f0" : "transparent",
                backgroundImage: displayConfig.transparentBackground
                  ? "repeating-conic-gradient(#d0d0d0 0% 25%, transparent 0% 50%) 50% / 16px 16px"
                  : "none",
              }}
            >
              {qrValue && (
                <BrandedQRCode
                  value={qrValue}
                  size={240}
                  style={{
                    foregroundColor: genie.branding?.qrCodeStyle?.foregroundColor || "#000000",
                    backgroundColor: genie.branding?.qrCodeStyle?.backgroundColor || "#FFFFFF",
                    cornerStyle: genie.branding?.qrCodeStyle?.cornerStyle || "square",
                    logoUrl: genie.branding?.logoUrl,
                    logoSize: genie.branding?.qrCodeStyle?.logoSize || 50,
                  }}
                  genie={genie}
                  displayConfig={displayConfig}
                />
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-center">Higher resolution when downloaded</p>

            {/* Invitations belong to the genie page; a product manual QR has no sign-up step. */}
            {!isProductTarget && (
              <div
                className={`mt-4 rounded-lg border-2 p-3 transition-all ${isInviteMode ? "border-gray-200 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10" : "border-gray-200 bg-white"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <p
                      className={`text-sm font-semibold ${isInviteMode ? "bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent" : "text-gray-700"}`}
                    >
                      Customer Invitation QR
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Scanning this QR will prompt the user to register and auto-add this genie to their favorites.
                    </p>
                  </div>
                  <Switch checked={isInviteMode} onCheckedChange={setIsInviteMode} />
                </div>
              </div>
            )}

            <button
              onClick={() => { onClose(); navigate(`/genie-hub/activation/genie/${genie?.id}#activate-invite`); }}
              className="mt-4 w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <span>View QR scan history</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </button>
          </div>
        </div>


      </div>
    </CoreDrawer>
  );
};

export default QRCodeModal;
